import { useEffect, useState, useCallback } from "react";
import { Loader2, Tag, History, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const CATS = [
  { key: "clinic", l: "c_clinic" },
  { key: "doctor_comp", l: "c_doctor_comp" },
  { key: "internal_lab", l: "c_internal_lab" },
  { key: "external_lab", l: "c_external_lab" },
];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—");

export default function Pricing() {
  const t = useT();
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("clinic");
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [history, setHistory] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/pricing/rates").then((r) => setRates(r.data.rates || {}))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (category, item_key) => {
    const d = drafts[`${category}:${item_key}`] || {};
    if (d.rate === undefined || d.rate === "" || Number(d.rate) < 0) { toast.error(t("pricing.enterValidRate")); return; }
    setBusy(`${category}:${item_key}`);
    try {
      await api.post("/pricing/rates", { category, item_key, rate: Number(d.rate), reason: d.reason || null });
      toast.success(t("pricing.rateSaved"));
      setDrafts((s) => ({ ...s, [`${category}:${item_key}`]: {} }));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const openHistory = async (category, item_key, item_label) => {
    try {
      const r = await api.get(`/pricing/rates/history?category=${category}&item_key=${item_key}`);
      setHistory({ item_label, rows: r.data });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1100px]">
      <PageHeader title={t("pricing.title")}
        subtitle={t("pricing.subtitle")} />
      <div className="mb-4 flex items-center gap-2 text-xs text-slate-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        <Lock className="h-3.5 w-3.5" /> {t("pricing.lockNote")}
      </div>
      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div> : (
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="pricing-tabs">
            {CATS.map((c) => <TabsTrigger key={c.key} value={c.key} data-testid={`pricing-tab-${c.key}`}>{t(`pricing.${c.l}`)}</TabsTrigger>)}
          </TabsList>
          {CATS.map((c) => (
            <TabsContent key={c.key} value={c.key}>
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`pricing-table-${c.key}`}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr className="text-left">
                      <th className="px-4 py-2 font-medium">{t("pricing.item")}</th>
                      <th className="px-4 py-2 font-medium">{t("pricing.currentRate")}</th>
                      <th className="px-4 py-2 font-medium">{t("pricing.effectiveFrom")}</th>
                      <th className="px-4 py-2 font-medium">{t("pricing.newRate")}</th>
                      <th className="px-4 py-2 font-medium">{t("pricing.reason")}</th>
                      <th className="px-4 py-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rates[c.key] || []).map((r) => {
                      const dk = `${c.key}:${r.item_key}`;
                      const d = drafts[dk] || {};
                      return (
                        <tr key={r.item_key} className="border-t border-slate-50" data-testid={`pricing-row-${c.key}-${r.item_key}`}>
                          <td className="px-4 py-2 font-medium text-slate-800 flex items-center gap-2"><Tag className="h-3.5 w-3.5 text-slate-400" />{r.item_label}</td>
                          <td className="px-4 py-2 tabular-nums" data-testid={`pricing-current-${c.key}-${r.item_key}`}>€{Number(r.rate).toFixed(2)}</td>
                          <td className="px-4 py-2 text-slate-400 text-xs">{fmt(r.effective_from)}</td>
                          <td className="px-4 py-2">
                            <Input type="number" min="0" step="0.01" value={d.rate ?? ""} placeholder="0.00"
                              className="h-8 w-28" data-testid={`pricing-input-${c.key}-${r.item_key}`}
                              onChange={(e) => setDrafts((s) => ({ ...s, [dk]: { ...d, rate: e.target.value } }))} />
                          </td>
                          <td className="px-4 py-2">
                            <Input value={d.reason ?? ""} placeholder={t("pricing.reasonOptional")} className="h-8 w-40"
                              onChange={(e) => setDrafts((s) => ({ ...s, [dk]: { ...d, reason: e.target.value } }))} />
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <Button size="sm" className="bg-navy hover:bg-navy-soft text-white h-8" disabled={busy === dk}
                              data-testid={`pricing-save-${c.key}-${r.item_key}`} onClick={() => save(c.key, r.item_key)}>
                              {busy === dk ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 ml-1" data-testid={`pricing-history-${c.key}-${r.item_key}`}
                              onClick={() => openHistory(c.key, r.item_key, r.item_label)}><History className="h-4 w-4" /></Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={!!history} onOpenChange={(v) => !v && setHistory(null)}>
        <DialogContent data-testid="pricing-history-dialog">
          <DialogHeader><DialogTitle>{t("pricing.historyTitle")} {history?.item_label}</DialogTitle>
            <DialogDescription>{t("pricing.historyDesc")}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-slate-500 text-left"><tr><th className="py-1">{t("pricing.rate")}</th><th>{t("pricing.effective")}</th><th>{t("pricing.by")}</th><th>{t("pricing.reason")}</th></tr></thead>
              <tbody>
                {(history?.rows || []).map((h) => (
                  <tr key={h.id} className="border-t border-slate-50">
                    <td className="py-1 tabular-nums">€{Number(h.rate).toFixed(2)}{h.active ? "" : ` ${t("pricing.inactive")}`}</td>
                    <td className="text-slate-500">{fmt(h.effective_from)}</td>
                    <td className="text-slate-500">{h.created_by}</td>
                    <td className="text-slate-400">{h.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
